import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import produce from 'immer';
import { get, groupBy, keyBy, set } from 'lodash/fp';
import type { FormState } from 'redux-form';

import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import type {
  InquiryTemplateEditorFormData,
  ScreenDimensions,
  UploadedAsset,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { VersionDiff } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { InquiryTemplateVersionError } from 'dashboard/lib/interfaces/InquiryTemplateVersionErrors';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionSchema,
  PartialInquiryTemplateVersion,
} from 'dashboard/models/inquiry-template-version';
import { MatchRequirementProfile } from 'dashboard/models/match-requirement-profile';
import { Template } from 'dashboard/models/template';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import {
  InquiryTemplateActions,
  InquiryTemplateActionType,
} from 'dashboard/redux/actions/inquiry-template';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import type { StepPreview, StepUi } from 'lib/interfaces/StepsConfig';
import { UiComponent } from 'lib/interfaces/ui-components';

export type InquiryTemplateSchema = {
  workflowVersions: Record<string, WorkflowVersion>;
  verificationTemplateVersions: Record<string, VerificationTemplateVersion>;
  matchRequirementProfiles: Record<string, MatchRequirementProfile[]>;
};

export type InquiryTemplateTreeData = {
  inquiryTemplateVersion: InquiryTemplateVersion;
  // Workflow and verification template versions used in the inquiry template version.
  schema: InquiryTemplateSchema;
};

export interface InquiryTemplateState {
  // tree
  inquiryTemplate: InquiryTemplate | Template | undefined;
  inquiryTemplateTreeData: InquiryTemplateTreeData;
  stepsPreview: StepPreview[];

  // screen editor
  assets: UploadedAsset[];
  validationErrors: InquiryTemplateVersionError[];
  locale: InquiryIso6391LanguageCode;
  stepLocalizedStrings: StepLocalizedStrings;

  // diff visualization
  comparedVersion: InquiryTemplateVersion | undefined;
  versionDiff: VersionDiff | undefined;
  comparedSchema: {
    workflowVersions: Record<string, WorkflowVersion>;
    verificationTemplateVersions: Record<string, VerificationTemplateVersion>;
    matchRequirementProfiles: Record<string, MatchRequirementProfile[]>;
  };

  // versions
  localDraftPartialInquiryTemplateVersion: PartialInquiryTemplateVersion;
  restoredFrom: string;

  // history
  editorHistory: EditorHistory;

  // screen dimensions
  editorScreenDimensions: ScreenDimensions;
  maxEditorScreenDimensions: ScreenDimensions;
}

export const initialInquiryTemplateState: InquiryTemplateState = {
  inquiryTemplate: undefined,
  inquiryTemplateTreeData: {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    inquiryTemplateVersion: undefined,
    schema: {
      workflowVersions: {},
      verificationTemplateVersions: {},
      matchRequirementProfiles: {},
    },
  },
  stepsPreview: [],

  // screen editor
  assets: [],
  validationErrors: [],
  locale: 'en-US',
  stepLocalizedStrings: {},

  // diff visualization
  comparedVersion: undefined,
  versionDiff: undefined,
  comparedSchema: {
    workflowVersions: {},
    verificationTemplateVersions: {},
    matchRequirementProfiles: {},
  },

  // versions
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  localDraftPartialInquiryTemplateVersion: undefined,
  // FIXME: Once we have "restored from" surfaced from the backend, this state
  // simply live on `inquiryTemplateVersion`
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  restoredFrom: undefined,

  // history
  editorHistory: {
    items: [],
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    current: undefined,
    historyIndex: 0,
  },

  // screen dimensions
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  editorScreenDimensions: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  maxEditorScreenDimensions: undefined,
};

// tree
interface SetInquiryTemplatePayload {
  inquiryTemplate: InquiryTemplate | Template;
}

interface SetTemplateTreeDataPayload {
  inquiryTemplateVersion: InquiryTemplateVersion;
  schema: InquiryTemplateSchema;
}

interface SetStepsPreviewPayload {
  stepsPreview: StepPreview[];
}

interface SetComponentPreviewPayload {
  componentPath: string;
  componentPreview: UiComponent;
  stepName: string;
}

// screen editor
interface SetAssetsPayload {
  assets: UploadedAsset[];
}

interface SetValidationErrorsPayload {
  validationErrors: InquiryTemplateVersionError[];
}

interface SetLocalePayload {
  locale: InquiryIso6391LanguageCode;
}

export interface LocaleStrings {
  locale: InquiryIso6391LanguageCode;
  // map of component names to client string key-value pairs
  strings: Record<string, Record<string, string>>;
}

export interface StepLocalizedStrings {
  [stepName: string]: Partial<
    Record<
      InquiryIso6391LanguageCode,
      { [componentName: string]: { [componentKey: string]: string } }
    >
  >;
}

interface SetStepLocalizedStringsPayload {
  stepLocalizedStrings: StepLocalizedStrings;
}

// diff visualization
interface SetComparedVersionPayload {
  comparedVersion: InquiryTemplateVersion;
}

interface SetComparedSchemaPayload {
  comparedSchema: InquiryTemplateVersionSchema;
}

interface SetVersionDiffDataPayload {
  versionDiff: VersionDiff;
}

// versions
interface SetLocalDraftPartialInquiryTemplateVersionPayload {
  localDraftPartialInquiryTemplateVersion: PartialInquiryTemplateVersion;
  restoredFrom: string;
}

// history
export interface EditorHistory {
  items: EditorHistoryEntry[];
  current: EditorHistoryEntry;
  historyIndex: number;
}

export interface EditorHistoryEntry {
  formData: InquiryTemplateEditorFormData;
  time: number;
  isFinal?: boolean;
}

interface SetEditorHistoryPayload {
  items: EditorHistoryEntry[];
  current?: EditorHistoryEntry;
  historyIndex: number;
}

export interface SetEditorScreenDimensionsPayload {
  screenDimensions: ScreenDimensions;
}

export interface SetMaxEditorScreenDimensionsPayload {
  screenDimensions: ScreenDimensions | undefined;
}

export const formReducer = {
  [INQUIRY_TEMPLATE_EDITOR_FORM_NAME]: (state: FormState, action: InquiryTemplateActions) => {
    switch (action.type) {
      case InquiryTemplateActionType.ClearFormValues: {
        return {
          ...state,
          values: null,
        };
      }
      case InquiryTemplateActionType.SetFormValues: {
        return {
          ...state,
          values: {
            ...state.values,
            ...action.payload.values,
          },
        };
      }
      default: {
        return state;
      }
    }
  },
};

const { reducer, actions } = createSlice({
  name: 'inquiry-templates',
  initialState: initialInquiryTemplateState,
  reducers: {
    clearInquiryTemplate: () => {
      return initialInquiryTemplateState;
    },
    // tree
    setInquiryTemplate: (state, action: PayloadAction<SetInquiryTemplatePayload>) => {
      state.inquiryTemplate = action.payload.inquiryTemplate;
    },
    setInquiryTemplateTreeData: (state, action: PayloadAction<SetTemplateTreeDataPayload>) => {
      const { inquiryTemplateVersion, schema } = action.payload;
      state.inquiryTemplateTreeData = { inquiryTemplateVersion, schema };
    },
    setStepsPreview: (state, action: PayloadAction<SetStepsPreviewPayload>) => {
      state.stepsPreview = action.payload.stepsPreview;
    },
    setComponentPreview: (state, action: PayloadAction<SetComponentPreviewPayload>) => {
      const { componentPath, componentPreview, stepName } = action.payload;
      state.stepsPreview = state.stepsPreview.map((stepPreview) =>
        stepPreview.name === stepName
          ? produce(stepPreview as StepUi, (draftStepPreview) => {
              const splitComponentPath = componentPath.split('.');
              const componentArrayPath = splitComponentPath.splice(0, -1).join('.');
              const insertIndex = parseInt(splitComponentPath.at(-1) || '');

              const componentPreviewArray = componentArrayPath
                ? get(componentArrayPath, draftStepPreview.config.components)
                : draftStepPreview.config.components;

              // either insert the preview if previously didn't exist or update the existing preview
              if (componentPreviewArray[insertIndex]?.name === componentPreview.name) {
                componentPreviewArray[insertIndex] = componentPreview;
              } else {
                componentPreviewArray.splice(insertIndex, 0, componentPreview);
              }

              draftStepPreview.config.components = set(
                componentArrayPath,
                componentPreviewArray,
                draftStepPreview.config.components,
              );
            })
          : stepPreview,
      );
    },
    // screen editor
    setAssets: (state, action: PayloadAction<SetAssetsPayload>) => {
      state.assets = action.payload.assets;
    },
    setValidationErrors: (state, action: PayloadAction<SetValidationErrorsPayload>) => {
      state.validationErrors = action.payload.validationErrors;
    },
    setLocale: (state, action: PayloadAction<SetLocalePayload>) => {
      state.locale = action.payload.locale;
    },
    setStepLocalizedStrings: (state, action: PayloadAction<SetStepLocalizedStringsPayload>) => {
      return {
        ...state,
        stepLocalizedStrings: {
          ...state.stepLocalizedStrings,
          ...Object.keys({
            ...action.payload.stepLocalizedStrings,
          }).reduce(
            (stepLocalizedStringsState, stepName) => ({
              ...stepLocalizedStringsState,
              [stepName]: {
                ...stepLocalizedStringsState[stepName],
                ...action.payload.stepLocalizedStrings[stepName],
              },
            }),
            state.stepLocalizedStrings,
          ),
        },
      };
    },
    // diff visualization
    setComparedVersion: (state, action: PayloadAction<SetComparedVersionPayload>) => {
      state.comparedVersion = action.payload.comparedVersion;
    },
    setComparedSchema: (state, action: PayloadAction<SetComparedSchemaPayload>) => {
      return {
        ...state,
        comparedSchema: {
          ...state.comparedSchema,
          // We overwrite instead of merging to avoid stale state
          workflowVersions: keyBy('id', action.payload.comparedSchema['workflow-versions'].data),
          verificationTemplateVersions: keyBy(
            'id',
            action.payload.comparedSchema['verification-template-versions'].data,
          ),
          matchRequirementProfiles: groupBy(
            'object-id',
            action.payload.comparedSchema['match-requirement-profiles'].data,
          ),
        },
      };
    },
    setVersionDiff: (state, action: PayloadAction<SetVersionDiffDataPayload>) => {
      state.versionDiff = action.payload.versionDiff;
    },
    // versions
    setLocalDraftPartialInquiryTemplateVersion: (
      state,
      action: PayloadAction<SetLocalDraftPartialInquiryTemplateVersionPayload>,
    ) => {
      const { localDraftPartialInquiryTemplateVersion, restoredFrom } = action.payload;
      state.localDraftPartialInquiryTemplateVersion = localDraftPartialInquiryTemplateVersion;
      state.restoredFrom = restoredFrom;
    },
    // history
    setEditorHistory: (state, action: PayloadAction<SetEditorHistoryPayload>) => {
      const { items, current, historyIndex } = action.payload;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      state.editorHistory = { items, current, historyIndex };
    },
    // editor screen dimensions
    setEditorScreenDimensions: (state, action: PayloadAction<SetEditorScreenDimensionsPayload>) => {
      state.editorScreenDimensions = action.payload.screenDimensions;
    },
    setMaxEditorScreenDimensions: (
      state,
      action: PayloadAction<SetMaxEditorScreenDimensionsPayload>,
    ) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      state.maxEditorScreenDimensions = action.payload.screenDimensions;
    },
  },
});

export const {
  clearInquiryTemplate,
  setInquiryTemplate,
  setInquiryTemplateTreeData,
  setStepsPreview,
  setComponentPreview,
  setAssets,
  setValidationErrors,
  setLocalDraftPartialInquiryTemplateVersion,
  setLocale,
  setStepLocalizedStrings,
  setComparedVersion,
  setComparedSchema,
  setVersionDiff,
  setEditorHistory,
  setEditorScreenDimensions,
  setMaxEditorScreenDimensions,
} = actions;

export const inquiryTemplateReducer = reducer;
